// ============================================
// File: src/components/teams/hooks/useUpcomingEvents.js
// Live subscribe to teams/{id}/events and expand recurring series
// ============================================
import { useEffect, useState } from 'react';
import { collection } from 'firebase/firestore';
import { safeOnSnapshot } from '../../../utils/firestoreSubscriptions';
import { db } from '../../../firebase-client';
import { listVisibleTeamEvents } from '../../../utils/boardApi';
import { getRecurrenceDatesForDay, getWeeklyRecurrenceScanLimit } from '../../../utils/recurrence';

function toOccurrenceId(dateLike) {
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseOccurrenceId(value) {
  if (typeof value !== 'string') return null;
  const v = value.trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null;
}

function splitCompoundId(value) {
  if (typeof value !== 'string' || !value.includes('__')) {
    return { baseId: value || '', occurrenceId: null };
  }
  const parts = value.split('__').filter(Boolean);
  if (!parts.length) return { baseId: value, occurrenceId: null };
  return {
    baseId: parts[0] || value,
    occurrenceId: parts.find((part) => parseOccurrenceId(part)) || null,
  };
}

function getEventBaseId(data, docId) {
  const explicit =
    typeof data?.originalEventId === 'string' ? data.originalEventId.trim() : '';
  if (explicit) return splitCompoundId(explicit).baseId || explicit;
  return splitCompoundId(docId).baseId || docId;
}

function getEventOccurrenceId(data, docId) {
  const compoundOccurrenceId =
    splitCompoundId(typeof data?.eventId === 'string' ? data.eventId : '').occurrenceId ||
    splitCompoundId(docId).occurrenceId ||
    null;
  if (compoundOccurrenceId) return compoundOccurrenceId;
  if (data?.scope === 'occurrence' || typeof data?.originalEventId === 'string') {
    return parseOccurrenceId(data?.occurrenceId) || parseOccurrenceId(data?.date) || null;
  }
  return parseOccurrenceId(data?.occurrenceId) || null;
}

function isOccurrenceOverrideDoc(data, docId) {
  const hasOccurrence = Boolean(getEventOccurrenceId(data, docId));
  if (!hasOccurrence) return false;
  if (data?.scope === 'occurrence') return true;
  if (typeof data?.originalEventId === 'string' && data.originalEventId.trim() !== '') return true;
  if (typeof data?.occurrenceId === 'string' && data.occurrenceId.trim() !== '') return true;
  return docId.includes('__');
}

function toStartForEvent(data, occurrenceId) {
  if (data?.date && data?.time) {
    const d = new Date(`${data.date}T${data.time}`);
    if (!Number.isNaN(d.getTime())) return d;
  }
  if (data?.start?.toDate) {
    const d = data.start.toDate();
    if (!Number.isNaN(d.getTime())) return d;
  }
  if (data?.start) {
    const d = new Date(data.start);
    if (!Number.isNaN(d.getTime())) return d;
  }
  if (occurrenceId) {
    const inferredTime =
      (typeof data?.time === 'string' && data.time.includes(':') ? data.time : null) ||
      (typeof data?.recurrence?.time === 'string' && data.recurrence.time.includes(':')
        ? data.recurrence.time
        : null) ||
      '00:00';
    const d = new Date(`${occurrenceId}T${inferredTime}`);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
}

function normalizeLocation(data) {
  return data?.location || {
    name: data?.locationName || '',
    street: data?.locationStreet || '',
    city: data?.locationCity || '',
    postcode: data?.locationPostcode || '',
    country: data?.locationCountry || '',
  };
}

function expandEvents(docs, now) {
  const results = [];
  const occurrenceOverrides = new Map();

  docs.forEach((docSnap) => {
    const data = docSnap.data() || {};
    const status = String(data.status || '').toLowerCase();
    const duration = Number(data.durationMinutes || 60) * 60 * 1000;
    const mode = data.mode || data.eventMode || 'inPerson';
    const onlineUrl = data.onlineUrl || '';
    const name = data.name || data.title || 'Untitled';
    const docId = docSnap.id;
    const occurrenceId = getEventOccurrenceId(data, docId);
    const originalId = getEventBaseId(data, docId);
    const isOccurrenceOverride = Boolean(originalId) && isOccurrenceOverrideDoc(data, docId);

    if (isOccurrenceOverride) {
      const start = toStartForEvent(data, occurrenceId);
      if (!start || Number.isNaN(start.getTime())) return;

      occurrenceOverrides.set(`${originalId}__${occurrenceId}`, {
        ...data,
        id: `${originalId}__${occurrenceId}`,
        originalId,
        originalEventId: originalId,
        occurrenceId,
        name,
        title: name,
        start,
        end: data.end?.toDate ? data.end.toDate() : new Date(start.getTime() + duration),
        type: 'oneOff',
        mode,
        onlineUrl,
        location: normalizeLocation(data),
      });
      return;
    }

    if (status === 'cancelled') return;

    const scheduleType = String(data.type || '').toLowerCase();
    const isOneOff = scheduleType === 'oneoff' || Boolean(data.date && data.time);
    const isRecurring = scheduleType === 'recurring';

    if (isOneOff) {
      const start = toStartForEvent(data, occurrenceId);
      if (!start || Number.isNaN(start.getTime())) return;
      results.push({
        ...data,
        id: docId,
        originalId: docId,
        originalEventId: docId,
        occurrenceId: null,
        name,
        title: name,
        start,
        end: data.end?.toDate ? data.end.toDate() : new Date(start.getTime() + duration),
        type: 'oneOff',
        mode,
        onlineUrl,
        location: normalizeLocation(data),
      });
      return;
    }

    if (!isRecurring || !Array.isArray(data.recurrence?.byDay) || !data.recurrence?.time) return;

    const dayCodes = Array.from(
      new Set(
        data.recurrence.byDay.filter((code) =>
          ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU'].includes(code)
        )
      )
    );
    const maxOccurrences = getWeeklyRecurrenceScanLimit({
      now,
      recurrenceEndDate: data.recurrence?.endDate,
      fallbackOccurrences: 12,
    });

    dayCodes.forEach((dayCode) => {
      const occurrenceDates = getRecurrenceDatesForDay({
        now,
        dayCode,
        recurrence: { ...data.recurrence, frequency: data.recurrence.frequency || data.frequency || 'WEEKLY', weekOfMonth: data.recurrence.weekOfMonth || data.weekOfMonth },
        maxOccurrences,
      });
      occurrenceDates.forEach((d, i) => {
        if (d < now) return;

        const recurringOccurrenceId = toOccurrenceId(d);
        results.push({
          ...data,
          id: recurringOccurrenceId
            ? `${docId}__${recurringOccurrenceId}`
            : `${docId}-${dayCode}-${i}`,
          originalId: docId,
          originalEventId: docId,
          occurrenceId: recurringOccurrenceId,
          name,
          title: name,
          start: new Date(d),
          end: new Date(d.getTime() + duration),
          type: 'recurring',
          mode,
          onlineUrl,
          location: normalizeLocation(data),
        });
      });
    });
  });

  const filtered = results.filter((entry) => {
    if (!entry.occurrenceId || !entry.originalId) return true;
    return !occurrenceOverrides.has(`${entry.originalId}__${entry.occurrenceId}`);
  });

  occurrenceOverrides.forEach((entry) => {
    if (String(entry.status || '').toLowerCase() === 'cancelled') return;
    filtered.push(entry);
  });

  return filtered.sort((a, b) => a.start - b.start);
}

export function useUpcomingEvents(id, enabled = true, isAdmin = false) {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    if (!id || !enabled) {
      setEvents([]);
      return;
    }

    if (!isAdmin) {
      let cancelled = false;
      listVisibleTeamEvents({ teamId: id })
        .then((result) => {
          if (cancelled) return;
          const docs = (Array.isArray(result?.events) ? result.events : []).map((eventData) => ({
            id: eventData.id,
            data: () => eventData,
          }));
          setEvents(expandEvents(docs, new Date()));
        })
        .catch(() => {
          if (!cancelled) setEvents([]);
        });

      return () => {
        cancelled = true;
      };
    }

    const unsub = safeOnSnapshot(
      collection(db, 'teams', id, 'events'),
      (qs) => {
        const now = new Date();
        setEvents(expandEvents(qs.docs, now));
      },
      (err) => {
        if (err?.code === 'permission-denied') return;
      }
    );

    return () => unsub();
  }, [id, enabled, isAdmin]);

  return events;
}
